import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion } from 'motion/react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Avatar,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

// Context for confirmation dialog
const ConfirmDialogContext = createContext(null);

// Default options
const defaultOptions = {
  title: 'Confirm Action',
  message: 'Are you sure you want to proceed?',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  variant: 'warning', // 'warning', 'danger', 'info'
};

// Hook to use the confirm dialog
export const useConfirm = () => {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmDialogProvider');
  }
  return context.confirm;
};

// Provider component
export const ConfirmDialogProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(defaultOptions);
  const [resolveRef, setResolveRef] = useState(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setOptions({ ...defaultOptions, ...opts });
      setResolveRef(() => resolve);
      setOpen(true);
    });
  }, []);

  const handleConfirm = () => {
    setOpen(false);
    if (resolveRef) {
      resolveRef(true);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    if (resolveRef) {
      resolveRef(false);
    }
  };

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}
      <ConfirmDialog
        open={open}
        title={options.title}
        message={options.message}
        confirmText={options.confirmText}
        cancelText={options.cancelText}
        variant={options.variant}
        hideCancel={options.hideCancel}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmDialogContext.Provider>
  );
};

// Original ConfirmDialog component (unchanged)
const ConfirmDialog = ({
  open,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'warning', // 'warning', 'danger', 'info'
  isLoading = false,
  hideCancel = false,
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          color: '#E74C3C',
          bgColor: '#FDF2F2',
          icon: <DeleteIcon sx={{ fontSize: 24 }} />,
        };
      case 'info':
        return {
          color: '#4698D3',
          bgColor: '#EBF5FB',
          icon: <InfoIcon sx={{ fontSize: 24 }} />,
        };
      case 'warning':
      default:
        return {
          color: '#F39C12',
          bgColor: '#FEF9E7',
          icon: <WarningIcon sx={{ fontSize: 24 }} />,
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          border: '1px solid #E5E5E5',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: styles.bgColor,
          borderBottom: '1px solid #E5E5E5',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 2,
          px: 3,
        }}
      >
        <Avatar
          sx={{
            backgroundColor: styles.color,
            width: 40,
            height: 40,
          }}
        >
          {styles.icon}
        </Avatar>
        <Typography
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            fontSize: '1.25rem',
            color: '#253E66',
          }}
        >
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: 3, px: 3 }}>
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
        <Typography
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontSize: '0.95rem',
            color: '#253E66',
            lineHeight: 1.6,
          }}
        >
          {message}
        </Typography>
        </motion.div>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: '1px solid #E5E5E5',
          backgroundColor: '#F9FBFD',
        }}
      >
        {!hideCancel && (
          <Button
            onClick={onCancel}
            disabled={isLoading}
            variant="outlined"
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '6px',
              borderColor: '#E5E5E5',
              color: '#666666',
              px: 3,
              '&:hover': {
                borderColor: '#999999',
                backgroundColor: '#F9FBFD',
              },
            }}
          >
            {cancelText}
          </Button>
        )}
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          variant="contained"
          sx={{
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '6px',
            px: 3,
            backgroundColor: variant === 'danger' ? '#E74C3C' : styles.color,
            '&:hover': {
              backgroundColor: variant === 'danger' ? '#C0392B' : styles.color,
              filter: 'brightness(0.9)',
            },
          }}
        >
          {isLoading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
